/**
 * Without-Abtretung patient checkout screen
 *
 * Route: `/webAppOnboarding/insurance-without-abtretung-patient-checkout`. Consent, legal
 * docs, and checkout table for the TK variant where Physiofit submits without IBAN and the
 * patient forwards the insurer's refund. No upfront payment required.
 *
 * Shared logic/presentation live in `useInsuranceCheckoutScreen` + `InsuranceCheckoutLayout`;
 * this screen only supplies the variant and its billing-detail drawer.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard` (callables use signed-in session).
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useInsuranceExplainBillingGuard('without_abtretung_patient')`; `useSaveOnboardingStep('insurance_without_abtretung_patient_checkout')`.
 */

import { InsuranceCheckoutLayout } from '../components/content/InsuranceCheckoutLayout';
import { InsuranceWithoutAbtretungPatientBillingDetailDrawer } from '../components/content/InsuranceWithoutAbtretungPatientBillingDetailDrawer';
import { useInsuranceCheckoutScreen } from '../hooks/useInsuranceCheckoutScreen';
import { isPatientSubmittingInsurer } from '../insurance/metaBuilders';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

export function WebOnboardingInsuranceWithoutAbtretungPatientCheckoutScreen() {
  const checkout = useInsuranceCheckoutScreen('without_abtretung_patient');
  const { insurance, insuranceShortName, billingMath, isBillingDetailOpen, setIsBillingDetailOpen } = checkout;

  const billingDetailDrawer =
    billingMath && insuranceShortName ? (
      <InsuranceWithoutAbtretungPatientBillingDetailDrawer
        open={isBillingDetailOpen}
        onClose={() => setIsBillingDetailOpen(false)}
        insuranceShortName={insuranceShortName}
        courseAmount={billingMath.courseAmount}
        refundRate={billingMath.refundRate}
        reimbursement={billingMath.reimbursement}
        hasBonus={Boolean(insurance?.bonus?.hasBonus)}
        patientSubmits={insurance ? isPatientSubmittingInsurer(insurance) : false}
      />
    ) : null;

  return <InsuranceCheckoutLayout checkout={checkout} billingDetailDrawer={billingDetailDrawer} />;
}
